import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  PaidOutlined as PaidIcon,
  StorefrontOutlined as StoreIcon,
  ConfirmationNumberOutlined as PassIcon,
  AddShoppingCartOutlined as BuyPassIcon,
  CheckCircleOutlined as SuccessIcon,
  HistoryOutlined as DebtIcon,
  EventOutlined as UpcomingIcon,
} from "@mui/icons-material";
import { useAuthStore } from "../store";
import LessonList from "../components/payments/LessonList";
import PassCard from "../components/payments/PassCard";
import { plural, formatDate, formatPln } from "../utils/paymentFormat";
import "../assets/style/PayForLesson.css";

// Authenticated in-app "what you owe" page reached from the warning banner /
// account badge. Unlike the public /pay-for-lesson/:token page (email link),
// this one is identity-based (Firebase auth) and shows EVERYTHING the logged-in
// user owes:
//   - ONLINE bucket  : future admin-signups + past unpaid lessons (debt) —
//                      payable in one PayU order, or (future signups only)
//                      settleable with an existing / freshly bought pass.
//   - ON-SITE bucket : past unpaid cash-on-site lessons — info only, paid in
//                      person.

const API = process.env.REACT_APP_BACKEND_ADDRESS;

const EMPTY_BUCKET = { count: 0, total_amount: 0, lessons: [] };

const MyPayments = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const fetchPendingSignups = useAuthStore((state) => state.fetchPendingSignups);

  const [loading, setLoading] = useState(true);
  const [owed, setOwed] = useState(null);
  const [purchasablePasses, setPurchasablePasses] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [coveringAll, setCoveringAll] = useState(false);
  const [buyingPassName, setBuyingPassName] = useState(null);
  const [payingLessonId, setPayingLessonId] = useState(null);
  const [notice, setNotice] = useState(null);

  const authConfig = useCallback(async () => {
    const idToken = await user.getIdToken();
    return { headers: { Authorization: `Bearer ${idToken}` } };
  }, [user]);

  const loadAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const cfg = await authConfig();
      const owedResp = await axios.get(`${API}/user/me/owed`, cfg);
      setOwed(owedResp.data);
      // Whether the user can cover any owed lesson with a live pass is derived
      // from the owed summary itself (`pass_coverable` per lesson), so there is
      // no separate /me/owed/passes call — the one-click "Pokryj karnetem"
      // action settles with all usable passes server-side.
      try {
        const buyResp = await axios.get(
          `${API}/user/me/owed/purchasable-passes`,
          cfg
        );
        setPurchasablePasses(Array.isArray(buyResp.data) ? buyResp.data : []);
      } catch {
        setPurchasablePasses([]);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(`Nie udało się pobrać informacji o płatnościach: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [user, authConfig]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const online = owed?.online || EMPTY_BUCKET;
  const onSite = owed?.on_site || EMPTY_BUCKET;
  const onlineCount = online.count || 0;
  const onSiteCount = onSite.count || 0;
  const nothingOwed = Boolean(owed) && (owed.total_count || 0) === 0;
  // A live pass can cover at least one owed lesson — derived straight from the
  // owed summary (no extra request). Drives the single "Pokryj karnetem" CTA.
  const hasCoverablePass = (online.lessons || []).some(
    (l) => l.pass_coverable === true
  );

  // Split the online bucket so the user can tell apart what they already owe
  // (past, unpaid = "dług") from upcoming admin-signups they will attend. The
  // backend already filtered the bucket (cutoff / cash-on-site / passes), so a
  // plain past-vs-future date split matches its debt-vs-pending semantics.
  const nowTs = Date.now();
  const onlineLessons = online.lessons || [];
  const debtLessons = onlineLessons.filter(
    (l) => new Date(l.lesson_start).getTime() < nowTs
  );
  const upcomingLessons = onlineLessons.filter(
    (l) => new Date(l.lesson_start).getTime() >= nowTs
  );
  const sumPrices = (list) => list.reduce((sum, l) => sum + (l.price || 0), 0);
  const debtTotal = sumPrices(debtLessons);
  const upcomingTotal = sumPrices(upcomingLessons);

  const handlePayAll = async () => {
    setSubmitting(true);
    setNotice(null);
    try {
      const cfg = await authConfig();
      const resp = await axios.post(`${API}/user/me/owed/pay`, null, cfg);
      const redirect = resp?.data?.redirect_url;
      if (redirect) {
        window.location.href = redirect;
      } else {
        setError("PayU nie zwrócił adresu płatności.");
        setSubmitting(false);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(`Nie udało się rozpocząć płatności: ${msg}`);
      setSubmitting(false);
    }
  };

  const handlePayOne = async (lessonId) => {
    setPayingLessonId(lessonId);
    setNotice(null);
    try {
      const cfg = await authConfig();
      const resp = await axios.post(
        `${API}/user/me/owed/pay-one`,
        { lesson_id: lessonId },
        cfg
      );
      const redirect = resp?.data?.redirect_url;
      if (redirect) {
        window.location.href = redirect;
      } else {
        setError("PayU nie zwrócił adresu płatności.");
        setPayingLessonId(null);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(`Nie udało się rozpocząć płatności: ${msg}`);
      setPayingLessonId(null);
    }
  };

  const handleCoverWithPass = async () => {
    setCoveringAll(true);
    setNotice(null);
    try {
      const cfg = await authConfig();
      const resp = await axios.post(
        `${API}/user/me/owed/use-pass-auto`,
        null,
        cfg
      );
      const settled = resp?.data?.settled_count || 0;
      setNotice(
        `Karnet pokrył ${settled} ${plural(
          settled,
          "termin",
          "terminy",
          "terminów"
        )}.`
      );
      await loadAll();
      // Keep the header badge / banner in sync with the new balance.
      fetchPendingSignups();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(`Nie udało się pokryć karnetem: ${msg}`);
    } finally {
      setCoveringAll(false);
    }
  };

  const handleBuyPass = async (passTypeName) => {
    setBuyingPassName(passTypeName);
    setNotice(null);
    try {
      const cfg = await authConfig();
      const resp = await axios.post(
        `${API}/user/me/owed/buy-pass`,
        { pass_type_name: passTypeName },
        cfg
      );
      const redirect = resp?.data?.redirect_url;
      if (redirect) {
        window.location.href = redirect;
      } else {
        setError("PayU nie zwrócił adresu płatności.");
        setBuyingPassName(null);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(`Nie udało się rozpocząć zakupu karnetu: ${msg}`);
      setBuyingPassName(null);
    }
  };

  const busy =
    submitting ||
    coveringAll ||
    buyingPassName !== null ||
    payingLessonId !== null;
  // Pass-first ordering: when the user can settle with a pass (use an existing
  // one or buy/renew), the pass options are the primary CTAs and the one-off
  // PayU payment is demoted to a secondary fallback below them.
  const hasPassOption = hasCoverablePass || purchasablePasses.length > 0;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 6 } }}>
      <Paper elevation={3} className="pay-for-lesson__paper">
        <Stack spacing={1} alignItems="center" textAlign="center">
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            nat•chnienie studio
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Twoje płatności
          </Typography>
        </Stack>

        <Box sx={{ mt: 3 }}>
          {loading && (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          )}

          {!loading && error && <Alert severity="warning">{error}</Alert>}

          {!loading && !error && nothingOwed && (
            <Stack spacing={2.5} alignItems="center" textAlign="center">
              <SuccessIcon color="success" sx={{ fontSize: 48 }} />
              <Typography variant="body1">
                Nie masz żadnych zaległości. Dziękujemy!
              </Typography>
              <Button variant="outlined" onClick={() => navigate("/")}>
                Wróć na stronę główną
              </Button>
            </Stack>
          )}

          {!loading && !error && !nothingOwed && owed && (
            <Stack spacing={2.5}>
              {notice && <Alert severity="info">{notice}</Alert>}

              {onlineCount > 0 && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1.5 }}>
                    Poniższe zajęcia możesz opłacić online — pojedynczo lub
                    wszystkie naraz.
                  </Typography>

                  {debtLessons.length > 0 && (
                    <Box sx={{ mb: upcomingLessons.length > 0 ? 2.5 : 0 }}>
                      <Divider sx={{ mb: 1.5 }}>
                        <Chip
                          icon={<DebtIcon />}
                          label="Zaległości"
                          color="warning"
                          size="small"
                        />
                      </Divider>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        Zajęcia, które już się odbyły, a nie zostały opłacone.
                      </Typography>
                      <LessonList
                        lessons={debtLessons}
                        totalLabel={`Zaległości (${debtLessons.length} ${plural(
                          debtLessons.length,
                          "termin",
                          "terminy",
                          "terminów"
                        )})`}
                        totalAmount={debtTotal}
                        onPayLesson={handlePayOne}
                        payingLessonId={payingLessonId}
                        payDisabled={busy}
                      />
                    </Box>
                  )}

                  {upcomingLessons.length > 0 && (
                    <Box>
                      <Divider sx={{ mb: 1.5 }}>
                        <Chip
                          icon={<UpcomingIcon />}
                          label="Nadchodzące zajęcia"
                          size="small"
                        />
                      </Divider>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        Zapisy na zajęcia, które dopiero się odbędą.
                      </Typography>
                      <LessonList
                        lessons={upcomingLessons}
                        totalLabel={`Nadchodzące (${upcomingLessons.length} ${plural(
                          upcomingLessons.length,
                          "termin",
                          "terminy",
                          "terminów"
                        )})`}
                        totalAmount={upcomingTotal}
                        onPayLesson={handlePayOne}
                        payingLessonId={payingLessonId}
                        payDisabled={busy}
                      />
                    </Box>
                  )}

                  {hasCoverablePass && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 2 }}>
                        <Chip
                          icon={<PassIcon />}
                          label="Pokryj karnetem"
                          size="small"
                        />
                      </Divider>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1.5 }}
                      >
                        Masz karnet, który pokryje Twoje zaległości — wykorzystasz
                        wejścia z karnetu, bez dodatkowych kosztów. Gdyby karnet
                        nie pokrył wszystkiego, pozostałe zajęcia opłać poniżej.
                      </Typography>
                      <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        onClick={handleCoverWithPass}
                        startIcon={
                          coveringAll ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <PassIcon />
                          )
                        }
                        disabled={busy}
                        className="pay-for-lesson__cta"
                      >
                        Pokryj karnetem
                      </Button>
                    </Box>
                  )}

                  {purchasablePasses.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Divider sx={{ mb: 2 }}>
                        <Chip
                          icon={<BuyPassIcon />}
                          label={hasCoverablePass ? "lub kup nowy karnet" : "Kup karnet"}
                          size="small"
                        />
                      </Divider>
                      <Stack spacing={1.5}>
                        {purchasablePasses.map((p) => (
                          <PassCard
                            key={p.pass_type_name}
                            title={p.pass_type_name}
                            caption={
                              <>
                                {formatPln(p.price)} · pokryje {p.covers_count}{" "}
                                {plural(
                                  p.covers_count,
                                  "termin",
                                  "terminy",
                                  "terminów"
                                )}
                                {p.valid_until_preview
                                  ? ` · ważny do ${formatDate(
                                      p.valid_until_preview
                                    )}`
                                  : p.valid_days
                                  ? ` · ważny ${p.valid_days} ${plural(
                                      p.valid_days,
                                      "dzień",
                                      "dni",
                                      "dni"
                                    )}`
                                  : ""}
                              </>
                            }
                            warning={
                              p.uncovered_other_category
                                ? `Ten karnet pokryje tylko ${(
                                    p.covered_lesson_names || []
                                  ).join(", ")}. Pozostałe zajęcia opłać innym karnetem lub gotówką na miejscu.`
                                : p.covers_count < onlineCount
                                ? "Karnet pokryje tylko część terminów. Pozostałe opłać online poniżej lub gotówką na miejscu."
                                : null
                            }
                            actionLabel="Kup karnet"
                            onAction={() => handleBuyPass(p.pass_type_name)}
                            disabled={busy}
                            loading={buyingPassName === p.pass_type_name}
                            buttonVariant="contained"
                            startIcon={<BuyPassIcon />}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* One-off PayU payment — primary when no pass option exists,
                      otherwise a secondary fallback below the pass options. */}
                  <Box sx={{ mt: 2 }}>
                    {hasPassOption && (
                      <Divider sx={{ mb: 2 }}>
                        <Chip
                          icon={<PaidIcon />}
                          label="lub zapłać jednorazowo"
                          size="small"
                        />
                      </Divider>
                    )}
                    <Button
                      variant={hasPassOption ? "outlined" : "contained"}
                      color="primary"
                      size="large"
                      fullWidth
                      onClick={handlePayAll}
                      startIcon={
                        submitting ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <PaidIcon />
                        )
                      }
                      disabled={busy}
                      className="pay-for-lesson__cta"
                    >
                      Zapłać wszystko przez PayU ({formatPln(online.total_amount)})
                    </Button>
                  </Box>
                </Box>
              )}

              {onSiteCount > 0 && (
                <Box>
                  {onlineCount > 0 && <Divider sx={{ mb: 2 }} />}
                  <Box className="pay-for-lesson__cash" sx={{ mb: 1.5 }}>
                    <StoreIcon
                      fontSize="small"
                      color="action"
                      sx={{ mt: "2px" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Te zajęcia opłać <strong>gotówką na miejscu</strong> — u
                      instruktora przed lub po zajęciach.
                    </Typography>
                  </Box>
                  <LessonList
                    lessons={onSite.lessons}
                    totalLabel={`Do zapłaty na miejscu (${onSiteCount} ${plural(
                      onSiteCount,
                      "termin",
                      "terminy",
                      "terminów"
                    )})`}
                    totalAmount={onSite.total_amount}
                    accentTotal={false}
                  />
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default MyPayments;
